import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { LIFESPAN_UNITS, SPECIES_STATUSES } from "@/lib/constants";
import { formatLifespan, parseLifespan } from "@/lib/infobox";

const one = (text) => (text ? [{ text, page_id: null, note: "" }] : []);
const selectCls = "h-8 rounded-md bg-canvas border border-line text-sm text-slate-200 px-2";

export const SpeciesStatusSelect = ({ fact, onChange, testId }) => (
  <select value={fact.items[0]?.text || "Extant"} onChange={(e) => onChange({ ...fact, items: one(e.target.value) })}
    className={`w-full ${selectCls}`} data-testid={`${testId}-species-status-select`}>
    {Object.keys(SPECIES_STATUSES).map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

export const SapientSwitch = ({ fact, onChange, testId }) => {
  const on = fact.items[0]?.text === "Sapient";
  return (
    <label className="flex items-center gap-3 px-1 cursor-pointer">
      <Switch checked={on} onCheckedChange={(v) => onChange({ ...fact, items: one(v ? "Sapient" : "Non-sapient") })} data-testid={`${testId}-sapient-switch`} />
      <span className={`text-sm transition-colors ${on ? "text-sky-300" : "text-slate-400"}`}>{on ? "Sapient" : "Non-sapient"}</span>
    </label>
  );
};

const KINDS = [["range", "Number / range"], ["immortal", "Immortal"], ["other", "Other"]];

export const LifespanEditor = ({ fact, onChange, testId }) => {
  const [l, setL] = useState(() => parseLifespan(fact.items[0]?.text));
  const update = (patch) => {
    const next = { ...l, ...patch };
    setL(next);
    onChange({ ...fact, items: one(formatLifespan(next)) });
  };
  const num = (k) => (e) => update({ [k]: e.target.value.replace(/[^\d.]/g, "") });
  return (
    <div className="space-y-2" data-testid={`${testId}-lifespan`}>
      <div className="flex flex-wrap gap-1">
        {KINDS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => update({ kind: l.kind === k ? "" : k })} aria-pressed={l.kind === k} data-testid={`${testId}-lifespan-${k}`}
            className={`text-[11px] font-code rounded-full border px-2.5 py-1 transition-colors ${l.kind === k ? "border-sky-500/50 bg-sky-500/15 text-sky-200" : "border-line text-slate-400 hover:text-slate-100"}`}>{label}</button>
        ))}
      </div>
      {l.kind === "range" && (
        <div className="flex items-center gap-1.5">
          <Input inputMode="decimal" value={l.min} onChange={num("min")} placeholder="80" className="h-8 w-16 text-sm bg-canvas" data-testid={`${testId}-lifespan-min`} />
          <span className="text-slate-500">–</span>
          <Input inputMode="decimal" value={l.max} onChange={num("max")} placeholder="120" className="h-8 w-16 text-sm bg-canvas" data-testid={`${testId}-lifespan-max`} />
          <select value={l.unit} onChange={(e) => update({ unit: e.target.value })} className={`flex-1 min-w-0 ${selectCls}`} data-testid={`${testId}-lifespan-unit`}>
            {LIFESPAN_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      )}
      {l.kind === "other" && (
        <Input value={l.other} maxLength={300} onChange={(e) => update({ other: e.target.value })} placeholder="Until their star burns out" className="h-8 text-sm bg-canvas" data-testid={`${testId}-lifespan-other-input`} />
      )}
    </div>
  );
};
